@let info = information();

@if (isLoading()) {
  <div class="flex justify-center py-12">
    <mat-spinner diameter="32" />
  </div>
} @else if (info && hasAnyContent()) {
  <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

    @if (info.shortDescriptionNl || info.placeNl || info.photos.length > 0 || info.expectations.length > 0 || info.appHeaderPhotoName || info.mapImageName || info.locationImageName || info.ambianceVideoImageName) {
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.information.tab.general' | translate }}</mat-card-title>
        </mat-card-header>
        <mat-card-content class="!pt-3 flex flex-col gap-2 text-sm">
          @if (info.shortDescriptionNl) {
            <p class="text-gray-700 dark:text-gray-300 line-clamp-2 italic border-l-2 border-gray-200 dark:border-gray-700 pl-2">
              {{ info.shortDescriptionNl }}
            </p>
          }
          @if (info.placeNl) {
            <div class="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <mat-icon class="!text-base !w-4 !h-4 !leading-none">location_on</mat-icon>
              <span>{{ info.placeNl }}</span>
            </div>
          }
          @if (info.photos.length > 0) {
            <div class="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <mat-icon class="!text-base !w-4 !h-4 !leading-none">photo_library</mat-icon>
              <span>{{ 'waters.information.photos-count' | translate: { count: info.photos.length } }}</span>
            </div>
          }
          @if (info.expectations.length > 0) {
            <div class="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <mat-icon class="!text-base !w-4 !h-4 !leading-none">checklist</mat-icon>
              <span>{{ 'waters.information.expectations-count' | translate: { count: info.expectations.length } }}</span>
            </div>
          }
          <div class="flex flex-col gap-1.5 pt-1 border-t border-gray-100 dark:border-gray-800 mt-1">
            @for (item of mediaItems(); track item.label) {
              <app-bool [value]="item.set" [label]="item.label | translate" />
            }
          </div>
        </mat-card-content>
      </mat-card>
    }

    @if (info.contentBlocks.length > 0) {
      <mat-card>
        <mat-card-header>
          <mat-card-title>
            {{ 'waters.information.tab.content-blocks' | translate }}
            <span class="ml-1 text-sm font-normal text-gray-400">({{ info.contentBlocks.length }})</span>
          </mat-card-title>
        </mat-card-header>
        <mat-card-content class="!pt-3 flex flex-col gap-1.5 text-sm text-gray-600 dark:text-gray-400">
          @for (entry of contentBlockStats(); track entry.labelKey) {
            <div class="flex items-center gap-2">
              <mat-icon class="!text-base !w-4 !h-4 !leading-none !text-gray-400">{{ entry.icon }}</mat-icon>
              <span>{{ entry.count }}× {{ entry.labelKey | translate }}</span>
            </div>
          }
        </mat-card-content>
      </mat-card>
    }

    @if (info.priceLabels.length > 0 || info.priceIntroductionNl || info.plainPriceSummaryNl || info.plainPriceServicesTextNl) {
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.information.tab.pricing' | translate }}</mat-card-title>
        </mat-card-header>
        <mat-card-content class="!pt-3 flex flex-col gap-2 text-sm">
          @if (info.priceIntroductionNl) {
            <p class="text-gray-700 dark:text-gray-300 line-clamp-2 italic border-l-2 border-gray-200 dark:border-gray-700 pl-2">
              {{ info.priceIntroductionNl }}
            </p>
          }
          @if (info.priceLabels.length > 0) {
            <div class="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <mat-icon class="!text-base !w-4 !h-4 !leading-none">sell</mat-icon>
              <span>{{ 'waters.information.price-labels-count' | translate: { count: info.priceLabels.length } }}</span>
            </div>
          }
          @for (item of pricingItems(); track item.label) {
            <app-bool [value]="item.set" [label]="item.label | translate" />
          }
        </mat-card-content>
      </mat-card>
    }

    @if (info.hotspots.length > 0 || info.hotspotIntroductionNl) {
      <mat-card>
        <mat-card-header>
          <mat-card-title>
            {{ 'waters.information.tab.hotspots' | translate }}
            @if (info.hotspots.length > 0) {
              <span class="ml-1 text-sm font-normal text-gray-400">({{ info.hotspots.length }})</span>
            }
          </mat-card-title>
        </mat-card-header>
        <mat-card-content class="!pt-3 flex flex-col gap-1.5 text-sm">
          @if (info.hotspotIntroductionNl) {
            <p class="text-gray-700 dark:text-gray-300 line-clamp-2 italic border-l-2 border-gray-200 dark:border-gray-700 pl-2 mb-1">
              {{ info.hotspotIntroductionNl }}
            </p>
          }
          @for (h of info.hotspots.slice(0, 4); track h.id) {
            <div class="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <mat-icon class="!text-sm !w-4 !h-4 !leading-none !text-gray-400">near_me</mat-icon>
              <span>{{ h.city || '—' }}@if (h.distanceToWater) {&nbsp;· {{ h.distanceToWater }} km}</span>
            </div>
          }
          @if (info.hotspots.length > 4) {
            <span class="text-xs text-gray-400">+{{ info.hotspots.length - 4 }} more</span>
          }
        </mat-card-content>
      </mat-card>
    }

    @if (info.locationCoordinates || info.locationAddressNl) {
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.information.tab.location' | translate }}</mat-card-title>
        </mat-card-header>
        <mat-card-content class="!pt-3 flex flex-col gap-2 text-sm text-gray-600 dark:text-gray-400">
          @if (info.locationCoordinates) {
            <div class="flex items-center gap-1.5">
              <mat-icon class="!text-base !w-4 !h-4 !leading-none">my_location</mat-icon>
              <span class="font-mono text-xs">{{ info.locationCoordinates }}</span>
            </div>
          }
          @if (info.locationAddressNl) {
            <div class="flex items-center gap-1.5">
              <mat-icon class="!text-base !w-4 !h-4 !leading-none">home</mat-icon>
              <span class="line-clamp-1">{{ info.locationAddressNl }}</span>
            </div>
          }
        </mat-card-content>
      </mat-card>
    }

    @if (info.metaDescriptionNl) {
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.information.tab.seo' | translate }}</mat-card-title>
        </mat-card-header>
        <mat-card-content class="!pt-3 text-sm">
          <p class="text-gray-700 dark:text-gray-300 line-clamp-3 italic">{{ info.metaDescriptionNl }}</p>
        </mat-card-content>
      </mat-card>
    }

  </div>
} @else {
  <div class="flex flex-col items-center justify-center py-12 text-center gap-2">
    <mat-icon class="!text-5xl !w-12 !h-12 !text-gray-300 dark:!text-gray-600">info_outline</mat-icon>
    <p class="text-sm font-medium text-gray-500 dark:text-gray-400">{{ 'waters.information.empty' | translate }}</p>
    <p class="text-xs text-gray-400 dark:text-gray-500">{{ 'waters.information.empty-hint' | translate }}</p>
  </div>
}
